import { useState } from 'preact/hooks';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import type { AuthMode, OnsiteProduct, OnsitePrice, TrialDiscount, ChargedTotals } from '@/types';
import type { StripeCheckoutSession } from '@stripe/stripe-js';
import { CheckoutShell } from '@/components/CheckoutShell';
import { OrderSummary } from '@/components/OrderSummary';
import { AccountConversion } from '@/components/AccountConversion';
import { AppliedPromoChip } from '@/components/PromoCodeInput';
import { appliedPromotion } from '@/components/PaymentForm';
import { couponChipLabel } from '@/components/promoDescriptor';
import { getPortalBase } from '@/api/client';
import { t, useT } from '@/i18n/t';

interface Props {
  // The catalog product/price behind the checkout. Absent when price-config
  // has not landed (or failed); the confirmation then omits the order-summary
  // sidebar.
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // The completed purchase's price type when no product/price is loaded (a
  // checkout that completed after a payment redirect or reload): it picks the
  // donation and subscription copy that product/price pick in-page.
  priceType?: string;
  amountCentsOverride?: number;
  // The paid path's final Checkout Session at confirm: the sidebar renders its
  // actual totals (discounts, tax) instead of the catalog price, and the
  // applied promo chip is read off it. Absent on the trial/convert paths,
  // which carry their promo via promoCode/discount instead.
  summary?: StripeCheckoutSession | null;
  // The promo applied on the trial form (or re-pinned on the trial-denial
  // acknowledgment), with its validated discount. Shown as a read-only chip
  // plus the sidebar discount note, so the confirmation names the discount the
  // buyer just got.
  promoCode?: string | null;
  discount?: TrialDiscount | null;
  // The convert-to-paid path's first-invoice totals (no session exists there):
  // the sidebar shows the actual charge and the discount as a line, not the
  // catalog price plus a future-tense note.
  charged?: ChargedTotals | null;
  // True when a returning member who logged in at the gate already held the
  // entitlement, so no payment was taken — the copy reflects that.
  alreadyEntitled?: boolean;
  // True when a free trial was just started (no charge today); the copy and the
  // order summary reflect the trial instead of a completed purchase.
  trial?: boolean;
  // The free-trial length in days, shown in the trial order summary.
  trialPeriodDays?: number;
  // Server-masked access email (ezAuth path only); display-only.
  maskedEmail?: string;
  // One-click portal login link minted server-side for a portal-eligible buyer
  // (verified ezAuth identity, or a brand-new visitor). When set, the "go to your
  // account" link logs the buyer straight into the portal with no magic-link
  // email; when absent it falls back to the bare portal URL (cold login).
  portalUrl?: string;
  // Set only when the just-completed purchase was a guest checkout and the
  // completion carried an email attestation. Its presence offers the optional
  // guest → account conversion CTA: the masked email for display and the
  // attestation the widget presents to sol's register. Absent for a logged-in
  // buyer.
  conversion?: { maskedEmail: string; emailAttestation: string };
  authMode?: AuthMode;
  domainId?: number;
  onClose: () => void;
  onDismiss?: () => void;
}

// The portal note adapts to the purchase: a recurring plan is framed as a
// subscription (cancel anytime), a one-time purchase as a receipt, and a
// returning member who already held access drops the "we emailed your receipt"
// line since no payment was taken. Absent price (price-config not yet loaded)
// falls through to the one-time copy.
function portalNote(isRecurring: boolean, alreadyEntitled: boolean): { heading: string; body: string; linkLabel: string } {
  const linkLabel = isRecurring ? t('success.portalLink.manage') : t('success.portalLink.account');
  if (alreadyEntitled) {
    const body = isRecurring
      ? t('success.portalBody.manageRecurring')
      : t('success.portalBody.manageOneTime');
    return { heading: t('success.portalHeading.manage'), body, linkLabel };
  }
  const body = isRecurring
    ? t('success.portalBody.receiptRecurring')
    : t('success.portalBody.receiptOneTime');
  return { heading: t('success.portalHeading.email'), body, linkLabel };
}

export function SuccessStep({ product, price, priceType, amountCentsOverride, summary, promoCode, discount, charged, alreadyEntitled = false, trial = false, trialPeriodDays, maskedEmail, portalUrl, conversion, authMode, domainId, onClose, onDismiss }: Props) {
  useT();
  const { inline, successUrl } = useCheckoutPresentation();
  const [conversionSkipped, setConversionSkipped] = useState(false);
  const sidebar = product && price
    ? <OrderSummary product={product} price={price} summary={summary} amountCentsOverride={amountCentsOverride} trialPeriodDays={trial ? trialPeriodDays : undefined} discount={discount} charged={charged} />
    : undefined;
  const purchasedPriceType = price?.type ?? priceType;
  const donation = product?.type === 'donation' || purchasedPriceType === 'donation_fixed' || purchasedPriceType === 'donation_variable';
  const note = portalNote(purchasedPriceType === 'recurring', alreadyEntitled);
  // The applied promo chip: read off the final session on the paid path, off
  // the carried code + validated discount on the trial/convert paths (a trial
  // has no session). No chip for an already-entitled return — no payment was
  // taken, so no promo was redeemed.
  // domainId lets appliedPromotion strip the domain namespace prefix from the
  // session's code string, so the chip shows the code the visitor entered.
  const sessionPromo = summary ? appliedPromotion(summary, domainId) : null;
  const chipCode = sessionPromo?.code ?? promoCode ?? null;
  // label is undefined for a carried code whose discount never validated
  // (ineligiblePromo.discount is nullable) — the chip then shows the bare code.
  const chipLabel = sessionPromo?.label ?? (price && promoCode && discount
    ? couponChipLabel(price, discount)
    : undefined);
  return (
    <CheckoutShell
      ariaLabel={successAriaLabel(alreadyEntitled, trial)}
      currentStep={3}
      sidebar={sidebar}
      onDismiss={onDismiss}
    >
      <div class="sm-eyebrow sm-eyebrow-icon sm-eyebrow-icon-success">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        {successEyebrow(alreadyEntitled, trial)}
      </div>
      <h2 class="sm-title">{successTitle(alreadyEntitled, trial)}</h2>
      <p class="sm-subtitle">{successSubtitle(alreadyEntitled, trial, donation)}</p>

      {maskedEmail && (
        <p class="sm-identity-note">{t('success.email')} <strong>{maskedEmail}</strong></p>
      )}

      {chipCode && (
        <div class="sm-promo notranslate">
          <div class="sm-promo-applied">
            <AppliedPromoChip code={chipCode} label={chipLabel} />
          </div>
        </div>
      )}

      <div class="sm-portal-note">
        <strong>{note.heading}</strong>
        <p>{note.body}</p>
        <a class="sm-portal-link" href={portalUrl ?? getPortalBase()} target="_blank" rel="noopener noreferrer">
          {note.linkLabel}
        </a>
      </div>

      {inline ? (
        <>
          {conversion && !conversionSkipped && <AccountConversion authMode={authMode} domainId={domainId} maskedEmail={conversion.maskedEmail} emailAttestation={conversion.emailAttestation} onContinue={() => setConversionSkipped(true)} />}
          {successUrl && (!conversion || conversionSkipped) && <a class="sm-btn sm-btn-primary" href={successUrl}>{t('cta.continue')}</a>}
        </>
      ) : conversion ? (
        // The conversion CTA owns the single continue/escape control (its
        // "No thanks, continue" link / post-create "Continue" button both call
        // onClose), so the success screen never shows a competing "Continue".
        <AccountConversion authMode={authMode} domainId={domainId} maskedEmail={conversion.maskedEmail} emailAttestation={conversion.emailAttestation} onContinue={onClose} />
      ) : (
        <button class="sm-btn sm-btn-primary" type="button" onClick={onClose}>
          {t('cta.continue')}
        </button>
      )}
    </CheckoutShell>
  );
}

function successAriaLabel(alreadyEntitled: boolean, trial: boolean): string {
  if (alreadyEntitled) return t('success.ariaLabel.member');
  if (trial) return t('success.ariaLabel.trial');
  return t('success.ariaLabel.paid');
}

function successEyebrow(alreadyEntitled: boolean, trial: boolean): string {
  if (alreadyEntitled) return t('success.eyebrow.member');
  if (trial) return t('success.eyebrow.trial');
  return t('success.eyebrow.paid');
}

function successTitle(alreadyEntitled: boolean, trial: boolean): string {
  if (alreadyEntitled) return t('success.title.member');
  if (trial) return t('success.title.trial');
  return t('success.title.paid');
}

function successSubtitle(alreadyEntitled: boolean, trial: boolean, donation: boolean): string {
  if (alreadyEntitled) return t('success.subtitle.member');
  if (trial) return t('success.subtitle.trial');
  if (donation) return t('success.subtitle.donation');
  return t('success.subtitle.paid');
}
